import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  changeTicketStatus,
  escalateTicket,
  planTicket,
  removeTicketEscalation,
  resumePlannedTicket,
  ticketActionFormQuery,
  toUtcStamp,
  updateTicket,
  type EscalationType,
  type TicketFormField,
  type TicketFormName,
} from '../api/easeedesk'
import { SelectPicker } from '../ui/SelectPicker'
import { SettingsCheck, SettingsRadio } from '../ui/SettingsParts'
import { ExternalProviderDetails } from './ExternalProviderModal'
import { ThirdPartyEmailField } from './ThirdPartyEmailField'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import './TicketActionForm.css'

/**
 * CE SE DESCHIDE SUB BUTOANELE FILEI "Change".
 *
 * In portal, fiecare buton cerea o adresa care intorcea o BUCATA DE MACHETA cu
 * un `<script>` inauntru, iar scriptul cerea apoi, cu inca o cerere, descrierea
 * campurilor. Aici se cere o data si se cere DESCRIEREA: serverul spune ce
 * campuri are formularul, ce se poate alege in fiecare si ce are tichetul acum
 * (`TicketActionForms`), si ecranul le deseneaza.
 *
 * De-aia e UN singur component pentru toate sase. Sase formulare desenate de
 * mana ar fi insemnat sase locuri in care sa se uite cineva sa treaca un camp
 * nou pe care si l-a adaugat clientul in setari - iar campurile alea chiar se
 * adauga, si nu de noi.
 *
 * ===========================================================================
 * REINCARCAREA
 * ===========================================================================
 *
 * Cateva alegeri schimba formularul insusi, si nu doar valoarea unui camp:
 *
 *  - PASUL, pe formularul de avansare. Fiecare pas de flux are alt formular, si
 *    portalul reincarca tot panoul la fiecare schimbare a sertarului de status
 *    (`reloadForm()` din `easeedesk.js`);
 *  - ECHIPA, pe escaladarea interna. Oamenii sunt ai echipei alese, si sertarul
 *    lor porneste gol (`_getUsersField()`);
 *  - CATEGORIA si SUBCATEGORIA, pe avansare si actualizare. Subcategoriile sunt
 *    ale categoriei, operatiile ale subcategoriei: fara reincarcare, sertarul de
 *    dedesubt ramanea cu lista categoriei de pe tichet (sau goala, pe un tichet
 *    fara categorie) oricat ai fi ales alta.
 *
 * Amandoua se cer inapoi de la server, ca acolo sta regula. Ce a completat omul
 * intre timp se pastreaza pentru campurile care raman - vezi `merge()`.
 *
 * ===========================================================================
 * REFUZURILE
 * ===========================================================================
 *
 * Un formular care nu se poate deschide vine cu `blocked` si cu motivul lui, si
 * motivul se arata ca atare. Portalul isi ascundea BUTONUL pe un tichet
 * rezolvat sau deja planificat, dar adresa raspundea oricui: formularul se
 * deschidea, omul il completa, si abia butonul de trimitere spunea nu.
 */

export function TicketActionForm({
  id,
  form,
  type,
  stopSla,
  onDone,
}: {
  id: number
  form: TicketFormName
  /** Felul escaladarii: 1 intern, 2 client, 3 furnizor din afara. */
  type?: number
  /** Planificarea care opreste SLA-ul ("Suspend service"). */
  stopSla?: boolean
  onDone?: () => void
}) {
  /* Cele doua alegeri care cer formularul din nou. Zero inseamna "cum vine de
     la server": primul pas legal, si sertarul de oameni gol. */
  const [step, setStep] = useState(0)
  const [group, setGroup] = useState(0)
  const [category, setCategory] = useState(0)
  const [subcategory, setSubcategory] = useState(0)
  /* De cate ori s-a salvat. Dupa o scriere, formularul porneste de la zero:
     pasul si echipa se cer din nou de la server, si campurile se golesc. */
  const [saved, setSaved] = useState(0)

  const { data, error, isFetching } = useQuery(ticketActionFormQuery(id, form, { type, stopSla, status: step, group, category, subcategory }))

  /*
   * Ce face portalul dupa "Save Next Step" (`submitForm.changeStatus()` din
   * `easeedesk.js`): `loadStatusTicketForm()` cere formularul de la capat -
   * fara pasul ales, ca sertarul sa porneasca pe STAREA NOUA a tichetului - si
   * reapasa butonul activ, ca panoul sa se refaca. Fara asta, cheia intrebarii
   * ramanea pe pasul vechi, iar formularul arata acelasi pas si dupa ce
   * tichetul trecuse de el.
   */
  function onSaved(): void {
    setStep(0)
    setGroup(0)
    setCategory(0)
    setSubcategory(0)
    setSaved((n) => n + 1)
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  if (data.blocked) {
    return (
      <div className="alert alert-info" role="alert">
        <i className="fas fa-info-circle" aria-hidden="true"></i> {data.blocked.message}
      </div>
    )
  }

  return (
    <Fields
      /* Cheia NU poarta pasul si echipa: la reincarcare starea completata
         trebuie sa ramana, si de aia se imbina in `merge()`. Poarta doar
         formularul, ca trecerea de la un buton la altul sa porneasca curat. */
      key={`${id}:${form}:${type ?? 0}:${saved}`}
      id={id}
      page={data}
      busy={isFetching}
      onStep={setStep}
      onGroup={setGroup}
      onCategory={(next) => {
        setCategory(next)
        setSubcategory(0)
      }}
      onSubcategory={setSubcategory}
      onSaved={onSaved}
      onDone={onDone}
    />
  )
}

export type Values = Record<string, string | string[]>

function Fields({
  id,
  page,
  busy,
  onStep,
  onGroup,
  onCategory,
  onSubcategory,
  onSaved,
  onDone,
}: {
  id: number
  page: import('../api/easeedesk').TicketActionForm
  busy: boolean
  onStep(next: number): void
  onGroup(next: number): void
  onCategory(next: number): void
  onSubcategory(next: number): void
  onSaved(): void
  onDone?: () => void
}) {
  const client = useQueryClient()
  const [values, setValues] = useState<Values>(() => initial(stepped(page)))
  const [files, setFiles] = useState<File[]>([])
  const [sending, setSending] = useState(false)

  /*
   * Formularul s-a reincarcat: campurile noi isi iau valoarea de la server, iar
   * cele care erau si inainte pastreaza ce a scris omul. Fara asta, o schimbare
   * de pas ar fi sters comentariul pe jumatate scris - si aia e munca pierduta
   * fara ca nimeni sa o fi cerut.
   */
  useEffect(() => {
    setValues((was) => merge(was, stepped(page)))
  }, [page])

  function set(key: string, next: string | string[]): void {
    setValues((was) => ({ ...was, [key]: next }))
  }

  /* Goleste un camp numai daca formularul il are: unul pus din senin ar pleca
     la trimitere si ar sterge de pe tichet ce formularul nici nu arata. */
  function clear(key: string): void {
    setValues((was) => (key in was ? { ...was, [key]: '' } : was))
  }

  function submit(): void {
    const empty = (page.fields ?? []).find((f) =>
      f.type === 'file_input' ? f.required && files.length === 0 : f.required && isEmpty(values[f.key]),
    )
    if (empty) {
      toast(`"${empty.label}" is required.`, 'Error', 'error')
      return
    }

    setSending(true)
    send(id, page, values, files)
      .then(() => {
        toast(`Ticket #${id} saved.`, 'Success', 'success')
        /* Fisa se reciteste intreaga: randul de sus, panoul de actiuni, filele
           si jurnalul poarta toate ce tocmai s-a schimbat. Lista sta sub
           `['easeedesk', 'table', ...]` (`ticketPageQuery`): randul isi ia
           starea si culoarea noua de acolo. */
        void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id] })
        void client.invalidateQueries({ queryKey: ['easeedesk', 'table'] })
        onSaved()
        onDone?.()
      })
      .catch((err) => toast(err instanceof Error ? err.message : 'Error saving', 'Error', 'error'))
      .finally(() => setSending(false))
  }

  /* Toate cinci sunt, in portal, un `form-horizontal` cu titlu deasupra
     (`TicketFormBuilder`, `UpdateTicketFormBuilder`, `PlanningTicketFormBuilder`,
     `EscalateTicketFormBuilder`): eticheta la dreapta pe un sfert din rand,
     campul pe restul, butonul de trimitere la dreapta. Asezarea e UNA, pe
     `.tk-action-form` (vezi foaia); clasa cu numele formularului ramane doar
     ca sa se poata gasi in pagina. */
  const planning = page.form === 'planning'
  const horizontal = page.form === 'update'
  const escalate = page.form === 'escalate'
  const advance = page.form === 'advance'

  return (
    <form
      className={`tk-action-form${planning ? ' tk-form-planning' : ''}${horizontal ? ' tk-form-update' : ''}${escalate ? ' tk-form-escalate' : ''}${advance ? ' tk-form-advance' : ''}`}
      onSubmit={(ev) => {
        ev.preventDefault()
        if (!sending) submit()
      }}
    >
      {planning && (
        <>
          <h4>Planning ticket</h4>
          <hr className="dotted" />
        </>
      )}
      {horizontal && (
        <>
          <h4>{page.name}</h4>
          <hr className="dotted" />
        </>
      )}
      {escalate && (
        <>
          <h4>Escalate ticket</h4>
          <hr className="dotted" />
        </>
      )}
      {advance && (
        <>
          <h4>Change status</h4>
          <hr className="dotted" />
        </>
      )}

      {(page.fields ?? []).map((field) => field.type === 'file_input' ? (
        /* `ticket_attachments` din formularul pasului - `$_FILES` in portal:
           fisierele pleaca in aceeasi cerere cu avansarea sau actualizarea. */
        <div className="form-group" key={field.key}>
          <label htmlFor={`f${id}-${field.key}`} className="control-label">
            {field.label} {field.required && <span className="required">*</span>}
          </label>
          <input
            id={`f${id}-${field.key}`}
            name={`${field.key}[]`}
            type="file"
            multiple
            className="form-control input-sm"
            onChange={(ev) => setFiles([...(ev.target.files ?? [])])}
          />
        </div>
      ) : field.key === 'planned_datetime' && page.duedate ? (
        /* `checkTicketPlanningDate`: avertismentul portalului cand data
           planificata trece de termenul tichetului. Nu opreste nimic. */
        <div key={field.key}>
          <Field id={id} field={field} value={values[field.key] ?? ''} onChange={(next) => set(field.key, next)} />
          {typeof values[field.key] === 'string' && (values[field.key] as string) >= page.duedate && (
            <div className="form-group">
              <span></span>
              <div className="alert alert-warning tk-planning-late">
                <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> Planned date exceeds the estimated time of resolving this ticket!
              </div>
            </div>
          )}
        </div>
      ) : field.key === 'external_provider' ? (
        /* `ExternalServiceProviderForm`: sub casuta, detaliile furnizorului ales
           (`#external_provider_details`) cu Edit, si Add pentru unul nou -
           ferestrele din `tickets/details/modals.php`. */
        <div key={field.key}>
          <Field id={id} field={field} value={values[field.key] ?? ''} onChange={(next) => set(field.key, next)} />
          <ExternalProviderDetails
            ticketId={id}
            providerId={Number(values[field.key] ?? 0) || 0}
            onPick={(picked) => set(field.key, String(picked))}
          />
        </div>
      ) : field.type === 'third_party_email' && field.email ? (
        /* "Waiting for Third Party": emailul catre furnizor, din sablon, in
           locul campului liber "Custom details" (`ThirdPartyEscalation`). */
        <ThirdPartyEmailField
          key={field.key}
          id={id}
          spec={field.email}
          providerId={Number(values.external_provider ?? 0) || 0}
          providerName={providerName(page, values)}
          onChange={(next) => set(field.key, next)}
        />
      ) : (
        <Field
          key={field.key}
          id={id}
          field={field}
          value={values[field.key] ?? ''}
          onChange={(next) => {
            set(field.key, next)
            /* Cele doua alegeri care cer formularul din nou. */
            if (field.key === 'ticket_status' && !Array.isArray(next)) onStep(Number(next) || 0)
            if (field.key === 'group' && !Array.isArray(next)) onGroup(Number(next) || 0)
            /* Categoria si subcategoria: lista de dedesubt e a alegerii noi, deci
               ce era ales acolo nu mai are sens si se goleste. */
            if (field.key === 'ticket_category' && !Array.isArray(next)) {
              clear('ticket_subcategory')
              clear('ticket_operation')
              onCategory(Number(next) || 0)
            }
            if (field.key === 'ticket_subcategory' && !Array.isArray(next)) {
              clear('ticket_operation')
              onSubcategory(Number(next) || 0)
            }
            /* Un buton de echipa rapida umple sertarul "Group" si ii cere
               oamenii, exact ca o alegere facuta de mana (`initQuickGroups()`). */
            if (field.type === 'quick_groups' && !Array.isArray(next)) {
              set('group', next)
              onGroup(Number(next) || 0)
            }
          }}
        />
      ))}

      <div className="form-group tk-form-submit">
        <button type="submit" className="btn btn-primary" disabled={sending || busy}>
          <i className={`fas ${advance ? 'fa-project-diagram' : 'fa-check'}`} aria-hidden="true"></i> {page.submit}
        </button>
      </div>
    </form>
  )
}

/* -------------------------------------------------------------------------- */
/*  UN CAMP                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Felurile de camp sunt cele din `helpers/form_fields_helper.php` - aceleasi
 * pe care le stie si ecranul de setari al campurilor proprii - plus doua pe
 * care si le cer numai formularele de aici: `multiple_text_inputs` (insirarea
 * "Affected" de pe planificare, care se citeste, nu se completeaza) si
 * `quick_groups` (randul de butoane de deasupra campului "Group").
 */
export function Field({
  id,
  field,
  value,
  onChange,
  missing = false,
}: {
  id: number
  field: TicketFormField
  value: string | string[]
  onChange(next: string | string[]): void
  /** Obligatoriu si gol la o incercare de trimitere: eticheta rosie si randul de sub camp. */
  missing?: boolean
}) {
  const name = `${field.key}-${id}`
  const group = missing ? 'form-group has-error' : 'form-group'
  const hint = missing ? <span className="help-block tk-field-missing">{field.label} is required.</span> : null
  const label = (
    <label htmlFor={name} className="control-label">
      {field.label} {field.required && <span className="required">*</span>}
    </label>
  )

  if (field.type === 'multiple_text_inputs') {
    /* `PlanningForms::_getAffectedValues()`: valorile puse, fara nume, adica un
       loc in care se CITESTE cine e atins. Nu pleaca nicaieri. */
    const rows = Array.isArray(field.value) ? field.value : []

    return (
      <div className={group}>
        {label}
        {rows.length === 0 ? (
          <p className="form-control-static text-muted">Nobody</p>
        ) : (
          rows.map((row, i) => <input key={i} type="text" className="form-control input-sm" value={row} readOnly />)
        )}
        {hint}
      </div>
    )
  }

  if (field.type === 'quick_groups') {
    return (
      <div className={`${group} tk-quick-groups`}>
        {label}
        <div className="quick-escalation-groups" role="group">
          {field.options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`btn btn-default btn-sm quick-escalation-group${value === String(option.value) ? ' btn-primary active' : ''}`}
              onClick={() => onChange(String(option.value))}
            >
              {option.name}
            </button>
          ))}
        </div>
        {hint}
      </div>
    )
  }

  if (field.type === 'textarea') {
    return (
      <div className={group}>
        {label}
        <textarea
          id={name}
          className="form-control"
          value={typeof value === 'string' ? value : ''}
          onChange={(ev) => onChange(ev.target.value)}
        />
        {hint}
      </div>
    )
  }

  if (field.type === 'select' || field.type === 'multiple_select' || field.type === 'affected_select') {
    /* Lista lui bootstrap-select, cum o porneste portalul pe `.select-selectpicker`
       (`initFormPlugins()` din `easeedesk.js`: liveSearch, size 10,
       `count > 1`), nu <select>-ul gol al browserului. `name` tine <select>-ul
       adevarat in pagina, cu id-ul lui, deci eticheta si probele il gasesc la
       fel; ce pleaca la trimitere e tot `values`, neschimbat. */
    const many = field.type === 'multiple_select'
    const options = field.options.map((option) => ({ value: String(option.value), label: option.name, subtext: option.subtext, group: option.group }))
    /* Lista goala arata un indemn gri (`.bs-placeholder`), nu o valoare: un
       "Required" scris in buton parea ales. Ca e obligatoriu spune asteriscul
       de la eticheta, si randul rosu de sub camp dupa o trimitere. */
    const empty = field.required ? `Select ${field.label.toLowerCase()}…` : '-'

    return (
      <div className={group}>
        {label}
        {many ? (
          <SelectPicker
            id={name}
            name={field.key}
            className="form-control input-sm"
            title="Nothing selected"
            size={10}
            multiple
            actionsBox
            selectedTextFormat="count > 1"
            options={options}
            value={Array.isArray(value) ? value : []}
            onChange={onChange}
          />
        ) : (
          <SelectPicker
            id={name}
            name={field.key}
            className="form-control input-sm"
            title={empty}
            size={10}
            /* Fara `required`, "-" ramane o alegere, ca in <select>-ul de dinainte:
               omul poate lasa campul gol dupa ce a ales ceva. */
            options={field.required ? options : [{ value: '', label: empty }, ...options]}
            value={typeof value === 'string' ? value : ''}
            onChange={onChange}
          />
        )}
        {hint}
      </div>
    )
  }

  if (field.type === 'checkbox') {
    return (
      <div className={group}>
        <div className="checkbox">
          <SettingsCheck id={name} checked={value === '1'} onChange={(on) => onChange(on ? '1' : '')} label={field.label} />
        </div>
        {hint}
      </div>
    )
  }

  if (field.type === 'radio') {
    return (
      <div className={group}>
        {label}
        {field.options.map((option) => (
          <div className="radio" key={option.value}>
            <SettingsRadio
              name={name}
              value={String(option.value)}
              checked={value === String(option.value)}
              onChange={() => onChange(String(option.value))}
              label={option.name}
            />
          </div>
        ))}
        {hint}
      </div>
    )
  }

  if (field.type === 'datetime_picker' || field.type === 'date_picker' || field.type === 'time_picker') {
    /* Ceasul vine si pleaca in UTC, fiindca in UTC il cer scrierile; browserul
       il arata si il primeste in ora omului, care e singurul loc unde fusul lui
       se stie sigur. */
    const kind = field.type === 'date_picker' ? 'date' : field.type === 'time_picker' ? 'time' : 'datetime-local'

    return (
      <div className={group}>
        {label}
        <input
          id={name}
          type={kind}
          className="form-control input-sm"
          value={toLocalInput(typeof value === 'string' ? value : '', kind)}
          onChange={(ev) => onChange(fromLocalInput(ev.target.value, kind))}
        />
        {hint}
      </div>
    )
  }

  return (
    <div className={group}>
      {label}
      <input
        id={name}
        type={field.type === 'number_input' ? 'number' : 'text'}
        className="form-control input-sm"
        value={typeof value === 'string' ? value : ''}
        onChange={(ev) => onChange(ev.target.value)}
      />
      {hint}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  TRIMITEREA                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Fiecare formular pleaca pe SCRIEREA lui, cea care exista deja.
 *
 * Nu pe un cap comun: scrierile astea nu sunt un singur lucru cu sase nume -
 * fiecare isi are portile, refuzurile si instiintarile ei, si trimit oameni
 * diferiti pe email. Un cap care le-ar fi imbinat le-ar fi si pierdut.
 */
function send(id: number, page: import('../api/easeedesk').TicketActionForm, values: Values, files: File[] = []): Promise<unknown> {
  const text = (key: string): string => {
    const v = values[key]

    return typeof v === 'string' ? v : ''
  }
  const num = (key: string): number => Number(text(key)) || 0
  const when = (key: string): Date | undefined => {
    const raw = text(key)

    return raw === '' ? undefined : new Date(`${raw.replace(' ', 'T')}Z`)
  }

  if (page.form === 'advance') {
    /* Pasul e cel din sertar, iar cand formularul nu deseneaza sertarul (un
       singur pas legal) e cel pe care l-a ales serverul. */
    return changeTicketStatus(id, { ticketStatus: num('ticket_status') || page.status, extra: values }, files)
  }

  if (page.form === 'update') {
    return updateTicket(id, values, files)
  }

  if (page.form === 'planning') {
    const at = when('planned_datetime')
    if (!at) return Promise.reject(new Error('Planned intervention time is required.'))

    return planTicket(id, {
      plannedAt: at,
      comments: text('comments') || undefined,
      notifier: num('notifier') || undefined,
      stopSla: page.stop_sla === 1,
    })
  }

  if (page.form === 'resume') {
    return resumePlannedTicket(id, text('comments'))
  }

  if (page.form === 'unescalate') {
    return removeTicketEscalation(id, text('comments') || undefined)
  }

  return escalateTicket(id, (page.escalation_type ?? 1) as EscalationType, {
    comments: text('comments') || undefined,
    email: thirdPartyEmail(text('third_party_email')),
    notifyAgent: text('notify_agent') !== '' && text('notify_agent') !== '0',
    group: num('group') || undefined,
    user: num('user') || undefined,
    ticketType: num('ticket_type') || undefined,
    customer: num('customer') || undefined,
    externalProvider: num('external_provider') || undefined,
    escalationReason: text('escalation_reason') || undefined,
    expiresAt: when('expiry_datetime'),
  })
}

/* -------------------------------------------------------------------------- */
/*  BUCATI MARUNTE                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Emailul catre furnizor, cum l-a lasat `ThirdPartyEmailField` (JSON), sau
 * nimic - si nimic cand furnizorul n-are adresa (`{noEmail: true}`): atunci
 * escaladarea pleaca fara email. Destinatarul nu e aici: il pune serverul.
 */
export function thirdPartyEmail(raw: string): { subject: string; body: string } | undefined {
  if (raw === '') return undefined
  try {
    const parsed = JSON.parse(raw) as { subject?: unknown; body?: unknown }
    if (typeof parsed.subject !== 'string' || typeof parsed.body !== 'string') return undefined

    return { subject: parsed.subject, body: parsed.body }
  } catch {
    return undefined
  }
}

/** Numele furnizorului ales, din sertarul formularului. */
function providerName(page: import('../api/easeedesk').TicketActionForm, values: Values): string {
  const chosen = String(values.external_provider ?? '')
  const option = (page.fields ?? []).find((f) => f.key === 'external_provider')?.options.find((o) => String(o.value) === chosen)

  return option?.name ?? ''
}

/**
 * Sertarul de status al avansarii porneste pe PASUL formularului (`page.status`).
 *
 * Optiunile lui sunt pasii urmatori, iar starea de acum a tichetului nu e
 * printre ei. Un server care trimite totusi starea de acum ca valoare (asa
 * facea `TicketActionForms` pana pe 5 octombrie 2026) lasa sertarul pe titlul
 * lui, "Required", si "Save Next Step" trimitea starea de acum drept pas. Aici
 * se pune pasul ales de server, ca ce se vede sa fie ce pleaca.
 */
export function stepped(page: import('../api/easeedesk').TicketActionForm): TicketFormField[] | undefined {
  if (page.form !== 'advance' || !page.status || !page.fields) return page.fields

  return page.fields.map((field) => {
    if (field.custom || field.key !== 'ticket_status') return field
    const known = field.options.some((option) => String(option.value) === String(field.value ?? ''))

    return known ? field : { ...field, value: String(page.status) }
  })
}

export function initial(fields: TicketFormField[] | undefined): Values {
  const out: Values = {}
  for (const field of fields ?? []) out[field.key] = field.value ?? ''

  return out
}

/**
 * Formularul reincarcat: campurile noi vin cu valoarea serverului, cele vechi
 * raman cum le-a lasat omul.
 */
export function merge(was: Values, fields: TicketFormField[] | undefined): Values {
  const out: Values = {}
  for (const field of fields ?? []) {
    out[field.key] = field.key in was ? was[field.key] : (field.value ?? '')
  }

  return out
}

export function isEmpty(value: string | string[] | undefined): boolean {
  if (value === undefined) return true

  return Array.isArray(value) ? value.length === 0 : value.trim() === ''
}

/** UTC "2026-09-10 14:00:00" -> ce astepta casuta, in ora omului. */
export function toLocalInput(utc: string, kind: string): string {
  if (utc === '') return ''

  const at = new Date(`${utc.replace(' ', 'T')}Z`)
  if (Number.isNaN(at.getTime())) return ''

  const pad = (n: number): string => String(n).padStart(2, '0')
  const date = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`
  const time = `${pad(at.getHours())}:${pad(at.getMinutes())}`

  if (kind === 'date') return date
  if (kind === 'time') return time

  return `${date}T${time}`
}

/** Si inapoi: ce a ales omul, in UTC, cum il cer scrierile. */
export function fromLocalInput(local: string, kind: string): string {
  if (local === '') return ''
  if (kind === 'time') return local

  const at = new Date(local)

  return Number.isNaN(at.getTime()) ? '' : toUtcStamp(at)
}
